import React, { PureComponent } from 'react';
import debounce from 'lodash.debounce';
import { max as d3Max } from 'd3-array';
import {
  scaleBand as d3ScaleBand
} from 'd3-scale';

import { getHighlightIndex } from '~/helper/utils';

import { setSelectedCountry } from '../../modules/App/AppState';
import YAxis from '../YAxis';
import BarChart from '../../components/BarChart';
import Bars from '../../components/BarChart/bars';
import XAxis from '../XAxis';

import Tooltip from '../Tooltip';

import './chartWrapper.styl';

class ChartWrapper extends PureComponent {
  constructor(props) {
    super();

    this.chartContainer = null;
    this.onResize = debounce(this.onResize.bind(this), 200);
    window.addEventListener('resize', this.onResize);

    this.staticDataSet = props.data.find(entry => entry.country_code === props.referenceCountry);

    const allValues = props.data.reduce((res, item) => {
      const values = item.values.map(val => val.value);
      values.forEach(val => res.push(val));
      return res;
    }, []);

    this.domain = [0, d3Max(allValues)];

    this.height = 500;
    this.margin = { top: 20, right: 10, bottom: 15, left: 10 };

    const barChartRange = [this.height - this.margin.bottom, 0];

    this.scaleY = d3ScaleBand()
      .range(barChartRange)
      .domain(this.staticDataSet.values.map(d => d.key))
      .paddingInner([0.1])
      .paddingOuter([0]);

    this.state = {
      width: 500,
      activeIncomeLayer: false,
      ttVisible: false,
      ttPos: {
        top: 0,
        left: 0
      }
    };

    window.comp = this;
  }

  componentDidMount() {
    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({ width: this.chartContainer.offsetWidth });
  }

  onResize() {
    if (this.state.width !== window.innerWidth) {
      this.setState({ width: this.chartContainer.offsetWidth });
    }
  }

  onCountrySelectChange = evt => this.props.dispatch(setSelectedCountry(evt.target.value));

  getValFromIncomeLayer = (dataSet) => {
    const currentValue = dataSet.incomeLayer.find(layer =>
      layer.key === this.state.activeIncomeLayer
    );
    return currentValue ? currentValue.value : '';
  }

  handleIncomeLayerEnter = (layer) => {
    this.setState({ activeIncomeLayer: layer, ttVisible: true });
  }

  handleIncomeLayerMove = (pos) => {
    const tooltipWidth = this.tooltip.props.width;

    const { right, left, width } = this.chartContainer.getBoundingClientRect();
    const newPos = {
      top: pos.top + window.pageYOffset,
      left: pos.left
    };
    // check if tooltip position is in horizontal view boundries
    const containerLeftBoundary = left;
    const containerRightBoundary = right;

    // in the left chart we will display the tooltip on the leftside of the pointer
    // in the right chart we will display the tooltip on the rightside of the pointer
    const isInLeftHalfOfContainer = (width / 2) >= pos.left - left;
    if (isInLeftHalfOfContainer) {
      newPos.left -= tooltipWidth;
    }

    const leftInBounds = containerLeftBoundary <= pos.left - tooltipWidth;
    const rightInBounds = containerRightBoundary >= pos.left + tooltipWidth;

    if (!leftInBounds) {
      newPos.left = containerLeftBoundary;
    } else if (!rightInBounds) {
      newPos.left = containerRightBoundary - tooltipWidth;
    }

    this.setState({ ttPos: newPos, ttVisible: true });
  }

  handleIncomeLayerLeave = () => {
    this.setState({ activeIncomeLayer: false, ttVisible: false });
  }

  render() {
    const currentDataSet = this.props.data.find(entry =>
      entry.country_code === this.props.selectedCountry
    );
    // axis width = 40
    const barChartWidth = (this.state.width / 2) - 40;

    return (
      <div className="chart-wrapper" ref={(ref) => { this.chartContainer = ref; }}>
        <svg className="chart__svg" width={this.state.width} height={this.height}>
          {this.state.width &&
            <g>
              <g className="bar__group" transform={`translate(${this.margin.left}, 0)`}>
                <BarChart
                  data={this.staticDataSet}
                  type="left"
                  width={barChartWidth}
                  height={this.height}
                  scaleY={this.scaleY}
                  domain={this.domain}
                  tranlateX={this.margin.left}
                  tranlateY={this.margin.top}
                  highlightIndex={getHighlightIndex(this.props.budget)}
                  activeIncomeLayer={this.state.activeIncomeLayer}
                  onIcomeLayerEnter={this.handleIncomeLayerEnter}
                  onIcomeLayerMove={this.handleIncomeLayerMove}
                  onIcomeLayerLeave={this.handleIncomeLayerLeave}
                  translate={this.props.translate}
                  currentLanguage={this.props.currentLanguage}
                />
                <Bars
                  data={this.staticDataSet}
                  type="left"
                  width={barChartWidth}
                  domain={this.domain}
                  scaleY={this.scaleY}
                  highlightIndex={getHighlightIndex(this.props.budget)}
                />
                <XAxis
                  items={this.staticDataSet.values}
                  type="left"
                  width={barChartWidth}
                  domain={this.domain}
                  tranlateX={0}
                  tranlateY={this.height}
                />
              </g>
              <YAxis
                items={this.staticDataSet.values}
                height={this.height}
                tranlateX={(this.state.width / 2)}
                data={currentDataSet}
                scaleY={this.scaleY}
                currentLanguage={this.props.currentLanguage}
              />
              <g className="bar__group" transform={`translate(${this.state.width - barChartWidth - this.margin.right}, 0)`}>
                <BarChart
                  data={currentDataSet}
                  type="right"
                  width={barChartWidth}
                  height={this.height}
                  scaleY={this.scaleY}
                  domain={this.domain}
                  tranlateX={this.state.width - barChartWidth - this.margin.right}
                  tranlateY={this.margin.top}
                  highlightIndex={getHighlightIndex(this.props.budget)}
                  activeIncomeLayer={this.state.activeIncomeLayer}
                  onIcomeLayerEnter={this.handleIncomeLayerEnter}
                  onIcomeLayerMove={this.handleIncomeLayerMove}
                  onIcomeLayerLeave={this.handleIncomeLayerLeave}
                  translate={this.props.translate}
                  currentLanguage={this.props.currentLanguage}
                />
                <Bars
                  data={currentDataSet}
                  type="right"
                  width={barChartWidth}
                  domain={this.domain}
                  scaleY={this.scaleY}
                  highlightIndex={getHighlightIndex(this.props.budget)}
                />
                <XAxis
                  items={currentDataSet.values}
                  type="right"
                  width={barChartWidth}
                  domain={this.domain}
                  tranlateX={0}
                  tranlateY={this.height}
                />
              </g>
            </g>
          }
        </svg>
        <Tooltip
          visible={this.state.ttVisible}
          top={this.state.ttPos.top}
          left={this.state.ttPos.left}
          layerTitle={this.props.translate(`layer.${this.state.activeIncomeLayer}`)}
          subTitle={this.props.translate(`layer.${this.state.activeIncomeLayer}_sub`)}
          countryLeft={this.props.referenceCountry}
          countryLeft_val={this.getValFromIncomeLayer(this.staticDataSet)}
          countryRight={this.props.selectedCountry}
          countryRight_val={this.getValFromIncomeLayer(currentDataSet)}
          width={240}
          height={100}
          ref={(ref) => { this.tooltip = ref; }}
        />
      </div>
    );
  }
}

export default ChartWrapper;



// WEBPACK FOOTER //
// ./src/components/ChartWrapper/index.js